import React, { useMemo, useState, useEffect, useRef } from "react";
import { useFocusContext } from "~/contexts/FocusContext";
import styles from "~/pages/CharacterSheet/widgets/RollSearch/RollSearch.scss";
import searchModalStyles from "~/widgets/SearchModal/SearchModal.module.scss";
import { Badge } from "~/widgets/Badge/Badge";
import { type Skill } from "~/pages/CharacterSheet/types";
import {
  type RollItem,
  getAllRollableItems,
  parseRollQuery,
  resolveRoll,
} from "~/utils/rollUtils";

export interface RollSearchProps {
  isOpen: boolean;
  onClose: () => void;
  skills: Skill[];
  coreStats: Record<string, number>;
  movement: number;
  onRoll: (name: string, poolSize: number, flatHits?: number) => void;
  /**
   * Name of the character being rolled for (optional, shown in header)
   */
  characterName?: string;
}

/**
 * RollSearch Component
 *
 * A floating search bar that allows users to quickly find and roll any character action.
 * Includes Skill Checks, Core Stats, and Movement.
 * Supports dice pool modifiers (e.g., "acc+2").
 *
 * Note: This component has custom filtering logic that requires direct control
 * over the query parsing, so it implements its own search modal rather than
 * using the generic SearchModal component.
 */
const RollSearch: React.FC<RollSearchProps> = ({
  isOpen,
  onClose,
  skills,
  coreStats,
  movement,
  onRoll,
  characterName,
}) => {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const { setIsOverlayOpen } = useFocusContext();

  // Notify FocusContext when overlay opens/closes
  useEffect(() => {
    setIsOverlayOpen(isOpen);
  }, [isOpen, setIsOverlayOpen]);

  // Combine all rollable items into a single searchable list
  const allRollableItems = useMemo((): RollItem[] => {
    return getAllRollableItems(coreStats, movement, skills);
  }, [skills, coreStats, movement]);

  const parsedQuery = useMemo(() => {
    return parseRollQuery(query);
  }, [query]);

  const filteredItems = useMemo(() => {
    if (!query.trim()) return allRollableItems;

    // Extract the search term (first word/stat before any operators)
    const searchTerm = query.match(/^[a-zA-Z]+/)?.[0]?.toLowerCase() || "";

    if (!searchTerm) {
      // If query starts with a number, offer a quick roll
      const numericMatch = query.match(/^\d+/);
      if (numericMatch) {
        const poolFromTerm = parseInt(numericMatch[0], 10);
        if (poolFromTerm > 0) {
          return [
            {
              name: "Quick Roll",
              formula: `${poolFromTerm}d6`,
              pool: poolFromTerm,
              category: "Manual",
            } as RollItem,
          ];
        }
      }
      return allRollableItems;
    }

    const matches = allRollableItems.filter((item) =>
      item.name.toLowerCase().includes(searchTerm)
    );

    // If no matches found and the term is a number, offer a quick roll
    if (matches.length === 0) {
      const isNumeric = /^\d+$/.test(searchTerm);
      if (isNumeric) {
        const poolFromTerm = parseInt(searchTerm, 10);
        if (poolFromTerm > 0) {
          return [
            {
              name: "Quick Roll",
              formula: `${poolFromTerm}d6`,
              pool: poolFromTerm,
              category: "Manual",
            } as RollItem,
          ];
        }
      }
    }

    return matches;
  }, [query, allRollableItems]);

  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setSelectedIndex(0);
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleRoll = (item?: RollItem) => {
    if (item) {
      // Try to resolve the query as a formula
      const resolved = resolveRoll(query, allRollableItems, coreStats);
      if (resolved) {
        onRoll(resolved.name, resolved.pool, resolved.flatHits);
      } else {
        // Fallback to the item directly
        onRoll(item.name, item.pool, parsedQuery.flatHits);
      }
    } else {
      // Try to resolve the raw query
      const resolved = resolveRoll(query, allRollableItems, coreStats);
      if (resolved) {
        onRoll(resolved.name, resolved.pool, resolved.flatHits);
      } else if (parsedQuery.flatHits !== 0) {
        // Standalone flat hits roll
        onRoll(`${parsedQuery.flatHits} hit${Math.abs(parsedQuery.flatHits) === 1 ? "" : "s"}`, 0, parsedQuery.flatHits);
      }
    }
    onClose();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % filteredItems.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filteredItems.length) % filteredItems.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredItems.length > 0) {
        handleRoll(filteredItems[selectedIndex]);
      } else {
        handleRoll();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className={searchModalStyles.overlay} onClick={onClose}>
      <div
        className={searchModalStyles.searchContainer}
        onClick={(e) => e.stopPropagation()}
      >
        {characterName && (
          <div className={styles.characterHeader}>
            Rolling for <span className={styles.characterName}>{characterName}</span>
          </div>
        )}
        <div className={searchModalStyles.inputWrapper}>
          <input
            ref={inputRef}
            type="text"
            className={searchModalStyles.searchInput}
            placeholder="Search roll (e.g. 'Mind' or 'Acrobatics +2')..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleKeyDown}
          />
          <kbd className={searchModalStyles.escHint}>ESC</kbd>
        </div>

        <ul className={searchModalStyles.resultsList}>
          {filteredItems.length > 0 ? (
            filteredItems.map((item, index) => {
              const isSelected = index === selectedIndex;

              // Try to resolve the query to get the actual pool that will be rolled
              const resolved = resolveRoll(query, allRollableItems, coreStats);
              const finalPool = resolved ? resolved.pool : item.pool;
              const displayFlatHits = resolved ? resolved.flatHits : parsedQuery.flatHits;

              return (
                <li
                  key={`${item.category}-${item.name}`}
                  className={`${searchModalStyles.resultItem} ${isSelected ? searchModalStyles.selected : ""}`}
                  onClick={() => handleRoll(item)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className={styles.resultItemContent}>
                    <div className={styles.skillInfo}>
                      <div className={styles.nameRow}>
                        <span className={`${styles.skillName} ${isSelected ? styles.skillNameSelected : ""}`}>
                          {item.name}
                        </span>
                        <Badge variant="category" selected={isSelected}>
                          {item.category}
                        </Badge>
                      </div>
                      <span className={styles.skillFormula}>{item.formula}</span>
                    </div>
                    <div className={`${styles.poolDisplay} ${isSelected ? styles.poolDisplaySelected : ""}`}>
                      {finalPool}d6
                      {displayFlatHits !== 0 && (
                        <span className={styles.flatHitsBadge}>
                          {displayFlatHits > 0 ? "+" : ""}{displayFlatHits} hit{Math.abs(displayFlatHits) === 1 ? "" : "s"}
                        </span>
                      )}
                      {resolved && resolved.name !== item.name && (
                        <span className={styles.modifierBreakdown}>
                          ({resolved.name})
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })
          ) : (
            <li className={searchModalStyles.noResults}>
              <div className={searchModalStyles.noResultsTitle}>No matches found</div>
              <div className={searchModalStyles.noResultsHint}>
                Try searching for a skill, attribute, or enter a pool size for a quick roll (e.g. "4" or "4 + 2 hits").
              </div>
            </li>
          )}
        </ul>

        <div className={searchModalStyles.footer}>
          <span>↑↓ to navigate</span>
          <span>↵ to roll</span>
        </div>
      </div>
    </div>
  );
};

export default RollSearch;
